import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Upload, Image, Building2, Settings, ImageIcon, ToggleRight, ToggleLeft, ArrowLeftIcon, ArrowRightIcon, Receipt } from 'lucide-react'
import { useTranslation } from '../../core/hooks/useTranslation'
import { useBusinessStore } from '../../core/stores/businessStore'
import { Business, BUSINESS_TYPES, BUSINESS_TYPE_LABELS } from '../../core/models/business'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs'
import Input from '../../components/ui/Input'
import Textarea from '../../components/ui/Textarea'
import Select from '../../components/ui/Select'
import SaveButton from '../../components/buttons/SaveButton'
import CancelButton from '../../components/buttons/CancelButton'
import { AppRoute } from '../../core/config/app-routes'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../core/stores/authStore'
import { UserRole } from '../../core/enums'

export default function BusinessEditPage() {
  const { businessId } = useParams<{ businessId: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const isSystemAdmin = user?.userRole === UserRole.SYSTEM_ADMIN
  const { t, isRTL } = useTranslation()
  const { fetchBusinesses, updateBusiness, createBusiness, uploadLogo } = useBusinessStore()
  const isCreateMode = businessId === 'create'

  const [business, setBusiness] = useState<Business | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    contactNumber: '',
    businessType: '',
    slug: '',
    isActive: true,
    isAcceptingOrders: true,
    taxFee: 0,
    color: '#1E293B'
  })
  const [errors, setErrors] = useState<{
    name?: string
    address?: string
    contactNumber?: string
    businessType?: string
    taxFee?: string
  }>({})

  // Logo upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Load business data
  useEffect(() => {
    const loadBusiness = async () => {
      if (!businessId) {
        navigate(AppRoute.BUSINESS_OWNER_BUSINESSES_ROUTE)
        return
      }

      // If create mode, skip loading
      if (isCreateMode) {
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      try {
        await fetchBusinesses()
        const businessIdNum = parseInt(businessId)
        // Get fresh businesses from store after fetch
        const { businesses: freshBusinesses } = useBusinessStore.getState()
        const foundBusiness = freshBusinesses.find(b => b.id === businessIdNum)

        if (!foundBusiness) {
          toast.error(t('businessNotFound') || 'Business not found')
          navigate(AppRoute.BUSINESS_OWNER_BUSINESSES_ROUTE)
          return
        }

        setBusiness(foundBusiness)
        setFormData({
          name: foundBusiness.name,
          description: foundBusiness.description || '',
          address: foundBusiness.address,
          contactNumber: foundBusiness.contactNumber,
          businessType: foundBusiness.businessType,
          slug: foundBusiness.slug || '',
          isActive: foundBusiness.isActive,
          isAcceptingOrders: foundBusiness.isAcceptingOrders,
          taxFee: foundBusiness.taxFee || 0,
          color: foundBusiness.color || '#1E293B'
        })
      } catch (error: any) {
        console.error('Failed to load business:', error)
        toast.error(t('error') || 'Failed to load business')
        navigate(AppRoute.BUSINESS_OWNER_BUSINESSES_ROUTE)
      } finally {
        setIsLoading(false)
      }
    }

    loadBusiness()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId])

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {}

    if (!formData.name.trim()) {
      newErrors.name = t('fieldRequired') || 'This field is required'
    }

    if (!formData.address.trim()) {
      newErrors.address = t('fieldRequired') || 'This field is required'
    }

    if (!formData.contactNumber.trim()) {
      newErrors.contactNumber = t('fieldRequired') || 'This field is required'
    }

    if (!formData.businessType) {
      newErrors.businessType = t('fieldRequired') || 'This field is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field: keyof typeof formData, value: string | boolean | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }))
    }
  }

  const handleAcceptingOrdersChange = async (value: string) => {
    if (!business) return

    const newValue = value === 'true'

    // Update local state immediately
    handleInputChange('isAcceptingOrders', newValue)
    setBusiness(prev => prev ? { ...prev, isAcceptingOrders: newValue } : null)

    try {
      // Save to backend immediately
      await updateBusiness(business.id, {
        isAcceptingOrders: newValue
      })

      toast.success(business.isAcceptingOrders ? t('notAcceptingOrders') : t('acceptingOrders'))

    } catch (error: any) {
      console.error('Failed to update accepting orders:', error)
      // Revert on error
      handleInputChange('isAcceptingOrders', !newValue)
      setBusiness(prev => prev ? { ...prev, isAcceptingOrders: !newValue } : null)
      if (!error?.response) {
        toast.error(error.message || t('error') || 'Failed to update orders status')
      }
    }
  }

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error(t('pleaseFillRequiredFields') || 'Please fill all required fields')
      return
    }

    setIsSubmitting(true)
    try {
      if (isCreateMode) {
        // Create new business
        const newBusiness = await createBusiness({
          name: formData.name,
          description: formData.description || undefined,
          address: formData.address,
          contactNumber: formData.contactNumber,
          businessType: formData.businessType as any
        })

        if (newBusiness) {
          toast.success(t('businessCreatedSuccessfully') || 'Business created successfully')
          navigate(AppRoute.BUSINESS_OWNER_BUSINESSES_ROUTE + '/' + newBusiness.id)
        }
      } else {
        // Update existing business
        if (!business) {
          toast.error(t('businessNotFound') || 'Business not found')
          return
        }

        const updated = await updateBusiness(business.id, {
          name: formData.name,
          description: formData.description || undefined,
          address: formData.address,
          contactNumber: formData.contactNumber,
          businessType: formData.businessType as any,
          isActive: formData.isActive,
          isAcceptingOrders: formData.isAcceptingOrders,
          color: formData.color
        })

        if (updated) {
          toast.success(t('businessUpdated'))
        }
      }
    } catch (error: any) {
      console.error(`Failed to ${isCreateMode ? 'create' : 'update'} business:`, error)
      if (!error?.response) {
        toast.error(error.message || t('error') || `Failed to ${isCreateMode ? 'create' : 'update'} business`)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSaveTaxFee = async () => {
    if (!business) return

    // Validate tax fee
    const taxFeeValue = Number(formData.taxFee)
    if (isNaN(taxFeeValue) || taxFeeValue < 0 || taxFeeValue > 100) {
      setErrors(prev => ({ ...prev, taxFee: t('invalidValue') || 'Invalid tax fee. Must be between 0 and 100' }))
      return
    }

    setIsSubmitting(true)
    try {
      await updateBusiness(business.id, {
        taxFee: taxFeeValue
      })
      // Reload business data
      await fetchBusinesses()
      const { businesses: freshBusinesses } = useBusinessStore.getState()
      const updated = freshBusinesses.find(b => b.id === business.id)
      if (updated) {
        toast.success(t('taxFeeUpdated'))
        setBusiness(updated)
        setFormData(prev => ({ ...prev, taxFee: updated.taxFee || 0 }))
      }
    } catch (error: any) {
      console.error('Failed to update tax fee:', error)
      if (!error?.response) {
        toast.error(error.message || t('error') || 'Failed to update tax fee')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(t('invalidImageFile') || 'Please select a valid image file')
        return
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error(t('fileTooLarge') || 'File size is too large. Maximum size is 2MB')
        return
      }

      setSelectedFile(file)

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setPreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleUploadLogo = async () => {
    if (!business || !selectedFile) return

    setIsUploadingLogo(true)
    try {
      const success = await uploadLogo(business.id, selectedFile)
      if (success) {
        toast.success(t('logoUploaded'))
        setSelectedFile(null)
        setPreview(null)
        // Reload business data
        await fetchBusinesses()
        // Get fresh businesses from store after fetch
        const { businesses: freshBusinesses } = useBusinessStore.getState()
        const updated = freshBusinesses.find(b => b.id === business.id)
        if (updated) {
          setBusiness(updated)
        }
      }
    } catch (error: any) {
      console.error('Failed to upload logo:', error)
      if (!error?.response) {
        toast.error(error.message || t('error') || 'Failed to upload logo')
      }
    } finally {
      setIsUploadingLogo(false)
    }
  }

  const handleCancel = () => {
    if (business) {
      navigate(AppRoute.BUSINESS_OWNER_BUSINESSES_ROUTE + '/' + business.id)
    } else {
      navigate(AppRoute.BUSINESS_OWNER_BUSINESSES_ROUTE)
    }
  }

  const businessTypeOptions = BUSINESS_TYPES.map(type => ({
    value: type,
    label: BUSINESS_TYPE_LABELS[type] || type
  }))

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!isCreateMode && !business) {
    return null
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center">
        <button
          onClick={handleCancel}
          className="px-2 py-0 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        >
          {isRTL ? <ArrowRightIcon className="w-5 h-5" /> : <ArrowLeftIcon className="w-5 h-5" />}
        </button>
        <p className="text-gray-700 mt-1">
          {isCreateMode ? t('createBusiness') : (business?.name || '')}
        </p>
      </div>

      {/* Tabs Container */}
      <Tabs defaultTab="basic">
        <TabsList variant="horizontal">
          <TabsTrigger value="basic" icon={<Building2 className="w-4 h-4" />}>
            {t('basicInformation') || 'Basic Information'}
          </TabsTrigger>
          {!isCreateMode && (
            <>
              <TabsTrigger value="logo" icon={<ImageIcon className="w-4 h-4" />}>
                {t('updateBusinessLogo') || 'Logo'}
              </TabsTrigger>
              <TabsTrigger value="taxFee" icon={<Receipt className="w-4 h-4" />}>
                {t('taxFee') || 'Tax Fee'}
              </TabsTrigger>
              <TabsTrigger value="settings" icon={<Settings className="w-4 h-4" />}>
                {t('settings') || 'Settings'}
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Basic Information Tab */}
        <TabsContent value="basic">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('basicInformation') || 'Basic Information'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Slug - Full Width - Only show in edit mode */}
                  {!isCreateMode && (
                    <div className="md:col-span-2">
                      <Input
                        label={t('slug') || 'Slug'}
                        type="text"
                        value={formData.slug || ''}
                        placeholder={t('slug') || 'Slug (e.g., my-business-name)'}
                        helperText={t('slugHelperText') || 'URL-friendly identifier for your business (cannot be changed)'}
                        disabled={true}
                        readOnly
                        required
                      />
                    </div>
                  )}

                  {/* Business Name - Full Width */}
                  <div className="md:col-span-2">
                    <Input
                      label={t('businessName')}
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder={t('businessName') || 'Business Name'}
                      error={errors.name}
                      required
                    />
                  </div>

                  {/* Description - Full Width */}
                  <div className="md:col-span-2">
                    <Textarea
                      label={t('businessDescription')}
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder={t('businessDescription') || 'Business Description'}
                      rows={4}
                    />
                  </div>

                  {/* Business Type */}
                  <div>
                    <Select
                      label={t('businessType')}
                      value={formData.businessType}
                      onChange={(e) => handleInputChange('businessType', e.target.value)}
                      options={businessTypeOptions}
                      placeholder={t('businessType') || 'Select Business Type'}
                      error={errors.businessType}
                      required
                    />
                  </div>

                  {/* Status */}
                  <div>
                    <Select
                      label={t('status')}
                      value={formData.isActive ? 'true' : 'false'}
                      onChange={(e) => handleInputChange('isActive', e.target.value === 'true')}
                      options={[
                        { value: 'true', label: t('active') },
                        { value: 'false', label: t('inactive') }
                      ]}
                    />
                  </div>

                  {/* Address - Full Width */}
                  <div className="md:col-span-2">
                    <Input
                      label={t('businessAddress')}
                      type="text"
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      placeholder={t('businessAddress') || 'Business Address'}
                      error={errors.address}
                      required
                    />
                  </div>

                  {/* Contact Number */}
                  <div>
                    <Input
                      label={t('businessPhone')}
                      type="tel"
                      value={formData.contactNumber}
                      onChange={(e) => handleInputChange('contactNumber', e.target.value)}
                      placeholder="01xxxxxxxxx"
                      error={errors.contactNumber}
                      required
                    />
                  </div>

                  {/* Business Color */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('businessColor')}
                    </label>
                    <div className="flex items-start gap-2">
                      <div className="relative">
                        <input
                          type="color"
                          value={formData.color}
                          onChange={(e) => handleInputChange('color', e.target.value)}
                          className="w-16 h-11 rounded-lg border-2 cursor-pointer"
                        />
                      </div>
                      <div className="flex-1">
                        <Input
                          type="text"
                          value={formData.color}
                          onChange={(e) => handleInputChange('color', e.target.value)}
                          placeholder="#1E293B"
                          helperText={t('businessColorHelperText')}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                  <SaveButton
                    onClick={handleSave}
                    isLoading={isSubmitting}
                    disabled={!formData.name.trim() || !formData.address.trim() || !formData.contactNumber.trim() || !formData.businessType || isSubmitting}
                  />
                  <CancelButton
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logo Tab */}
        <TabsContent value="logo">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('updateBusinessLogo') || 'Update Business Logo'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mx-auto space-y-6">
                {/* Current Logo Display */}
                {business?.logoUrl && !preview && (
                  <div className="text-center">
                    <div className="inline-block relative">
                      <div className="aspect-square w-64 mx-auto rounded-2xl overflow-hidden border-4 border-gray-100 shadow-xl">
                        <img
                          src={business.logoUrl}
                          alt="Current logo"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <p className="text-sm text-gray-500 mt-4">{t('currentLogo') || 'Current Logo'}</p>
                    </div>
                  </div>
                )}

                {/* Upload Area */}
                <div
                  className="relative border-2 border-dashed border-gray-300 rounded-2xl p-12 cursor-pointer hover:border-blue-500 transition-all bg-gradient-to-br from-gray-50 to-gray-100 hover:from-blue-50 hover:to-blue-100 group"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {preview ? (
                    <div className="space-y-4">
                      <div className="aspect-square w-64 mx-auto rounded-2xl overflow-hidden border-4 border-blue-300 shadow-xl">
                        <img
                          src={preview}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <p className="text-base font-semibold text-blue-600 text-center">{t('logoPreview') || 'New Logo Preview'}</p>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white mb-6 group-hover:bg-blue-50 transition-colors shadow-lg">
                        <Upload className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                      </div>
                      <p className="text-base font-semibold text-gray-700 mb-2">
                        {t('clickToSelectImage') || 'Click to select image'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {t('selectLogoImage') || 'PNG, JPG, JPEG - Max 2MB'}
                      </p>
                    </div>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {/* File Info & Upload Button */}
                {selectedFile && (
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-5">
                      <div className="flex items-center gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-blue-200 flex items-center justify-center">
                          <Image className="w-6 h-6 text-blue-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-base font-semibold text-gray-900 truncate">{selectedFile.name}</p>
                          <p className="text-sm text-gray-600 mt-1">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                      <SaveButton
                        onClick={handleUploadLogo}
                        isLoading={isUploadingLogo}
                        disabled={!selectedFile || isUploadingLogo}
                        label={t('uploadLogo') || 'Upload Logo'}
                      />
                      <CancelButton
                        onClick={handleCancel}
                        disabled={isUploadingLogo}
                      />
                    </div>
                  </div>
                )}

                {/* Action Buttons - Show when no file selected */}
                {!selectedFile && (
                  <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                    <CancelButton
                      onClick={handleCancel}
                      className="px-6 py-2 text-sm"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tax Fee Tab */}
        <TabsContent value="taxFee">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('taxFeeManagement') || 'Tax Fee Management'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <p className="text-sm text-gray-600 mb-6">
                    {t('taxFeeDescription') || 'Manage the tax rate applied to orders'}
                  </p>

                  <div className="max-w-md">
                    <Input
                      label={t('taxFee') || 'Tax Fee'}
                      type="number"
                      value={formData.taxFee.toString()}
                      disabled={!isSystemAdmin}
                      onChange={(e) => {
                        const value = e.target.value
                        handleInputChange('taxFee', value === '' ? 0 : Number(value))
                      }}
                      placeholder="0"
                      helperText={t('taxFeeHelperText') || 'Tax rate as a percentage (e.g., 5 means 5%)'}
                      error={errors.taxFee}
                      min="0"
                      max="100"
                      step="0.1"
                    />
                  </div>

                  {/* Example calculation */}
                  {formData.taxFee > 0 && (
                    <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm font-semibold text-gray-900 mb-2">
                        {t('example') || 'Example Calculation'}
                      </p>
                      <p className="text-sm text-gray-700">
                        {t('forOrderOf') || 'For an order of'} <span className="font-semibold">100 {t('currency') || 'EGP'}</span>,
                        {' '}{t('taxWillBe') || 'tax will be'} <span className="font-semibold">{(100 * formData.taxFee / 100).toFixed(2)} {t('currency') || 'EGP'}</span>
                        {' '}({formData.taxFee}%)
                      </p>
                      <p className="text-sm text-gray-700 mt-1">
                        {t('totalAmount') || 'Total amount'}: <span className="font-semibold">{(100 + (100 * formData.taxFee / 100)).toFixed(2)} {t('currency') || 'EGP'}</span>
                      </p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                {isSystemAdmin && (
                  <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                    <SaveButton
                      onClick={handleSaveTaxFee}
                      isLoading={isSubmitting}
                      disabled={isSubmitting || isNaN(Number(formData.taxFee)) || Number(formData.taxFee) < 0 || Number(formData.taxFee) > 100}
                    />
                    <CancelButton
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('settings') || 'Settings'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Accepting Orders Status */}
                <div className="flex items-center justify-between pt-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${formData.isAcceptingOrders ? 'bg-green-500' : 'bg-gray-400'
                      }`}></div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {formData.isAcceptingOrders ? t('acceptingOrders') : t('notAcceptingOrders')}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {formData.isAcceptingOrders ? t('acceptingOrders') : t('notAcceptingOrders')}
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => handleAcceptingOrdersChange(formData.isAcceptingOrders ? 'false' : 'true')}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${formData.isAcceptingOrders
                      ? 'text-green-700 bg-green-100 hover:bg-green-200'
                      : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                      }`}
                  >
                    <span>{formData.isAcceptingOrders ? t('stopAcceptingOrders') : t('startAcceptingOrders')}</span>
                    {formData.isAcceptingOrders ? (
                      <ToggleRight className="w-4 h-4" />
                    ) : (
                      <ToggleLeft className="w-4 h-4" />
                    )}
                  </button>
                </div>

              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

